import { definitions, operations, paths } from '../apitypes';
export type EntityState<T = null> = {
    data: T;
    isLoaded: boolean;
    isLoading: boolean;
    hasError: boolean;
    error: any;
    requestIsHidden: boolean;
};

export type ListState<T> = EntityState<T> & {
    data: T[];
    total?: number;
    take?: number;
    skip?: number;
    offset?: number;
};

export interface CreatedUpdated {
    Created: Date | string;
    Updated: Date | string;
}

export type Administrator = Required<definitions['CompanyUserQueryResponse']>;

export type AdministratorRole = Required<definitions['CompanyUserRolesQueryResponse']>;

// export type Booking = Required<definitions['BookingQueryResponse']>;

export interface BookingExternalRefence {
    CompanyId: string;
    ExternalData: any;
    OwnerId: string;
    Updated: string;
    Created: string;
    CreatedBy: string;
    ReferenceType: string;
}

export interface Booking {
    Id: number;
    CompanyId: string;
    From: Date | string;
    To: Date | string;
    Status: string;
    StatusId: number;
    StatusName: string;
    StatusInfo: BookingStatus;
    SendEmailReminder: boolean;
    SendSmsReminder: boolean;
    SendSmsConfirmation: boolean;
    SendEmailConfirmation: boolean;
    LastTimeToUnBook: Date;
    CustomFields: CustomField[];
    CustomFieldValues: CustomFieldValueType[];
    BookedResourceTypes: ResourceType[];
    Customer: Customer;
    Quantities: Quantity[];
    Service: Service;
    PaymentExpiration: Date;
    Log: Array<BookingLog>;
    PaymentLog?: Array<BookingPayment>;
    ExternalReference: BookingExternalRefence[];
    CheckoutLog: Array<{
        Id: string;
        BookingId: number;
        PurchaseId: number;
        ExpirationTime: Date;
        Snippet: string;
        Status: string;
        Created: Date;
        Updated: Date;
    }>;
    CalendarExportStatus: {
        CalendarId: string;
        BookingId: number;
        Synced: boolean;
    };
    LengthInMinutes: number;
    BookedBy: string;
    BookedComments: string;
    UnbookedComments: string;
    CommentsToCustomer: string;
    CreatedDate: Date;
    UnbookedOn: Date;
    Reason: string;
    Tags?: Tag[];
    GoogleMeetUrl?: string;
}

export interface BookingUserQueuePriceResponse {
    PriceText: string;
    Price: number;
    Quantity: number;
}

export interface BookingUserQueueItem {
    BookingUserQueueId: number;
    CustomerId: string;
    ServiceId: number;
    From: Date | string;
    To: Date | string;
    StatusCode: number;
    StatusName: string;
    Quantities: BookingUserQueuePriceResponse[];
    Service: Service;
    Company: any;
    Customer: Customer;
}

export interface BookingLog {
    Id: number;
    BookingId: number;
    EventTypeId: number;
    EventType: {
        Id: number;
        Name: string;
        Description: string;
    };
    Comments: string;
    UserName: string;
    Created: Date;
}

export interface BookingPayment {
    Id: number;
    BookingId: number;
    PaymentReferenceId: string;
    OrderItemReferenceId: string;
    PaymentProviderId: number;
    Amount: number;
    VAT: number;
    AmountCredited: number;
    CurrencyId: string;
    CurrencyInfo: {
        Id: string;
        Name: string;
        CurrencySign: string;
    };
    Comments: string;
    Created: Date;
    Updated: Date;
}

export interface BookingReportOption {
    Id: number;
    Name: string;
    HeaderLeftCell: string;
    HeaderMiddleCell: string;
    HeaderRightCell: string;
    BodyCell: string;
    FooterLeftCell: string;
    FooterMiddleCell: string;
    FooterRightCell: string;
}

export interface BookingStatus {
    Id: number;
    Name: string;
    Description: string;
    Icon: string;
    Color: string;
}

export interface FailedBooking {
    From: Date;
    To: Date;
    Quantities: Quantity[];
    Reason: string;
}

export interface BookingPrintoutTemplate {
    CompanyId: string;
    Id: number;
    Name: string;
    HeaderLeftCell: string;
    HeaderMiddleCell: string;
    HeaderRightCell: string;
    BodyCell: string;
    FooterLeftCell: string;
    FooterMiddleCell: string;
    FooterRightCell: string;
}

export interface BookingSettings {
    CompanyId: string;
    BookingStatusId: number;
    ScheduleViewId: number;
    BookingTemplateId: number;
    CalendarTypeId: number;
    AllowBookingOnUnbookedTimes: boolean;
    SendEmailReminder: boolean;
    SendSmsReminder: boolean;
    SendEmailConfirmation: boolean;
    SendSmsConfirmation: boolean;
    EmailReminderTime: number;
    SmsReminderTime: number;
    MaxActiveBookings: number;
    SendNotifications: boolean;
    SendNotificationsEmail: string;
    MessageText: string;
    EnableMobileApp: boolean;
    ScheduleStartTime: string;
    ScheduleEndTime: string;
    ReceiptTemplate: string;
    ScheduleTimeSlotMinutes: number;
    ScheduleGroupResources: boolean;
    ShowFreeTimesLeft: boolean;
    FreeSpotTextsId: number;
    EnableICalGroupBookings: boolean;
    AgreementTemplate: string;
    ScheduleShowTimeExeptions: boolean;
    EnableBookingsOnSameTime: boolean;
    ShowWeekNumberSettingId: number;
    EnableShowBookedTimes: boolean;
    EnableSendFollowUpMessage: boolean;
    BookSpotUserResponseMinutes: number;
    IsBookSpotDirectly: boolean;
    BookSpotDirectlyTimeLeftMinutes: number;
    SendEmailNotificationQueue: boolean;
    SendSMSNotificationQueue: boolean;
    BookOnlyOnExistingCustomers: boolean;
    ShowMultipleResourcesAsOne: boolean;
    ShowMultiDayAsTime: boolean;
    CustomerInvoiceAdressRequired: boolean;
    ShowPersonalIdentityNumber: boolean;
    PersonalIdentityNumberRequired: boolean;
    AutoGenerateUniquePinCode: boolean;
    RequireCustomerPinCode: boolean;
}

export interface CompanyCategory {
    Id: number;
    Name: string;
    Description: string;
    Header: string;
    ImageUrl: string;
    Active: boolean;
}

export interface CompanyType {
    Id: number;
    Name: string;
    Description: string;
}

export interface Coordinate {
    Longitude: number;
    Latitude: number;
}

export interface Country {
    Id: string;
    Name: string;
    Description: string;
}

// export interface Currency {
//     Id: string;
//     Name: string;
//     Description: string;
//     CurrencySign: string;
//     Active: boolean;
// }

export interface DateSchedule {
    Id: number;
    Name: string;
    Description: string;
    Active: boolean;
    IsResourceSpecific: boolean;
    ScheduleDates: [
        {
            Id: number;
            From: Date | string;
            To: Date | string;
        }
    ];
    Resources: [
        {
            Id: number;
            Name: string;
            ImageUrl: string;
        }
    ];
    Services: [
        {
            Id: number;
            Name: string;
            ImageUrl: string;
        }
    ];
    UpdatedDate: Date | string;
    CreatedDate: Date | string;
}

export interface FieldTranslation {
    Id: number;
    Code: string;
    Name: string;
    Description: string;
    Group: string;
}

export interface BookingPrintoutFieldTranslation extends FieldTranslation { }

export interface NewsletterFieldTranslation extends FieldTranslation { }

// eslint-disable-next-line no-redeclare
export interface BookingPrintoutTemplate {
    CompanyId: string;
    Id: number;
    Name: string;
    HeaderLeftCell: string;
    HeaderMiddleCell: string;
    HeaderRightCell: string;
    BodyCell: string;
    FooterLeftCell: string;
    FooterMiddleCell: string;
    FooterRightCell: string;
}

export type Company = definitions['Company'];

export type CustomFieldTables = 'Event' | 'BookedEvent' | 'RegisteredUsers';

export interface CustomFieldValueType {
    Id: number;
    Column: string;
    Name: string;
    Description: string;
    Value: string;
    DataType: string;
    DefaultValue: string;
}

export interface CustomField {
    Id: number;
    CompanyId: string;
    GroupId: number;
    FieldId: number;
    Name: string;
    Width: number;
    Column: string;
    Description: string;
    DataType: string;
    DefaultValue: string;
    IsMandatory: boolean;
    MandatoryErrorMessage: string;
    MaxLength: number;
    MultipleLineText: boolean;
    RegEx: string;
    RegExId: string;
    RegExErrorMessage: string;
    IsPublic: boolean;
    IsHidden: boolean;
    SortOrder: number;
    Values: CustomFieldValueType[];
    Services: Service[];
    Table: CustomFieldTables;
}

export interface CustomFieldSlot {
    Id: number;
    Table: 'Event';
    Name: string;
    Datatype: 'String';
    Occupied: boolean;
}

export interface CustomFieldValidation {
    Id: number;
    Name: string;
    Description: string;
    ErrorMessage: string;
    RegExCode: string;
}

export interface Customer {
    Id: string;
    CompanyId: string;
    Firstname: string;
    Lastname: string;
    Phone: string;
    Email: string;
    Password: string;
    CorporateIdentityNumber: string;
    PersonalIdentityNumber?: string;
    CustomFields: Array<CustomField>;
    CustomFieldValues: Array<CustomFieldValueType>;
    ImageUrl: string;
    InvoiceAddress1?: string;
    InvoiceCity?: string,
    InvoicePostalCode?: string;
    InvoiceCountryCode?: string;
    Tags?: Tag[];
}

export interface CustomerComment {
    Id: number;
    CustomerId: string;
    Comments: string;
    Updated: Date | string;
    Created: Date | string;
    ImageUrl: string;
}

export interface DashboardMessage {
    Id: number;
    Title: string;
    Body: string;
    Important: boolean;
    VisibleFrom: Date | string;
    VisibleTo: Date | string;
    Created: Date | string;
    MessageTypeId: number;
    SupportCaseId: number;
    MessageType: string;
}

export interface HomepageImage {
    CompanyId: string;
    Id: number;
    Title?: string;
    Description?: string;
    ImageUrl: string;
    SortOrder: number;
}

export interface HomepageMenu {
    CompanyId: string;
    HomeText: string;
    HomeHidden: boolean;
    ServicesText: string;
    ServicesHidden: boolean;
    BookTimeText: string;
    BookTimeHidden: boolean;
    AboutUsText: string;
    AboutUsHidden: boolean;
    ContactUsText: string;
    ContactUsHidden: boolean;
    MyBookingsText: string;
    MyBookingsHidden: boolean;
    CalendarText: string;
    CalendarHidden: boolean;
    NewsText: string;
    NewsHidden: boolean;
    AddressText: string;
    AddressHidden: boolean;
    GalleryText: string;
    GalleryHidden: boolean;
    CancelBookingText: string;
    CancelBookingHidden: boolean;
}

export interface HomepageSettings extends CreatedUpdated {
    CompanyId: string;
    HomepageHeading: string;
    WelcomePageHeading: string;
    WelcomePageBody: string;
    AboutUsPageHeading: string;
    AboutUsPageBody: string;
    ImageUrl: string;
    HomePageTemplateId: number;
    HeroSectionStyleId: number;
    ShowRating: boolean;
    HomePageTemplateOptions: [
        {
            Id: number;
            Name: string;
            Description: string;
            ImageUrl: {};
        }
    ];
    HomepageHeroSectionStyleOptions: [
        {
            Id: number;
            Name: string;
            Description: string;
        }
    ];
}

export interface HomepageWidgetSettings {
    CompanyId: string;
    ServiceLayoutId: number;
    TimeLayoutId: number;
    BookingLayoutId: number;
    PrimaryColor: string;
    ShowServiceImage: false;
    ShowNextAvailableTime: false;
    ShowEndTime: false;
    BookedTimeSlotText: string;
    ServiceLayoutOptions: {
        Id: number;
        Name: string;
        Description: string;
        Code: string;
    }[];
    TimeLayoutOptions: {
        Id: number;
        Name: string;
        Description: string;
        Code: string;
    }[];
    BookingLayoutOptions: {
        Id: number;
        Name: string;
        Description: string;
        Code: string;
    }[];
    BookingMethodOptions: BookingMethodOption[];
    EnableCreateAccount: boolean;
    EnableDirectBooking: boolean;
    EnableLogin: boolean;
    EnableFacebookLogin: boolean;
    primaryColorChoice?: 'default' | 'custom';
}

export interface BookingMethodOption {
    Id: number;
    Name: string;
    Description: string;
    Code: string;
}

export interface Invoice {
    InvoiceId: string;
    BookingId: number;
    Id: number;
    StatusId: number;
    InvoiceDate: Date | string;
    DueDate: Date | string;
    PeriodFrom: Date | string;
    PeriodTo: Date | string;
    TotalAmount: number;
    Rounding: number;
    VAT: number;
    VATRegistrationNo: string;
    RegistrationNumber: string;
    CompanyName: string;
    ContactPerson: string;
    Street1: string;
    Street2: string;
    City: string;
    Zip: string;
    CountryId: string;
    CurrencyId: string;
    Created: Date | string;
    Updated: Date | string;
    PromikBookInvoiceId: number;
    Message: string;
    CurrencyInfo: {
        Id: string;
        Name: string;
        CurrencySign: string;
    };
    InvoiceStatus: {
        Id: number;
        Name: string;
        Description: string;
    };
    InvoiceLines: [
        {
            InvoiceId: number;
            Id: number;
            PeriodFrom: Date | string;
            PeriodTo: Date | string;
            ArticleNumber: number;
            ArticleName: string;
            ArticleDescription: string;
            Quantity: number;
            UnitPrice: number;
            SubTotal: number;
            Rebate: number;
            Updated: Date | string;
            Created: Date | string;
        }
    ];
}

export enum LicensePlanId {
    Monthly = 1,
    Quarterly = 2,
    Yearly = 3,
}

export type LicensePlans = definitions['QueryResponse_LicensePlanQueryResponse_'];

export interface LicenseType {
    Id: number;
    Name: string;
    Description: string;
    IsExtraLicenseOption: boolean;
    PeriodOfNoticeDays: number;
    Items: Array<{
        Id: number;
        Name: string;
        AllowedItems: number;
    }>;
    Prices: definitions["LicensePrice"][];
}

export interface LogMessage {
    Id: number;
    BookingId: number;
    Receiver: string;
    MessageTitle: string;
    MessageBody: string;
    Created: Date | string;
    ToSendDate: Date | string;
    SentDate: Date | string;
    Sent: boolean;
    MessageRetries: number;
    SendMethodId: number;
}

export interface Newsletter {
    Id: number;
    Receiver: string;
    MessageTitle: string;
    MessageBody: string;
    Created: Date | string;
    ToSendDate: Date | string;
    SentDate: Date | string;
    Sent: boolean;
    MessageRetries: number;
    SendMethodId: number;
}

export interface MessageTemplate {
    Id: number;
    Name: string;
    Title: string;
    Body: string;
    Sender: string;
    Default: boolean;
    MessageType: MessageTemplateType;
}

export interface MessageTemplateType {
    Id: number;
    Name: string;
    Description: string;
    MaxCharacters: number;
    DefaultText: string;
    SendMethodId: number;
}

export interface NewsletterEmailTemplate {
    Id: number;
    Name: string;
    Description: string;
    Body: string;
}

export interface NewsletterTemplate {
    Id: number;
    Heading: string;
    Body: string;
    EmailTemplateId: number;
    SendMethodId: number;
    ImageUrl: string;
    Created: Date | string;
    SendMethodInformation: NewsletterSendMethod;
    EmailTemplateInformation: {
        Id: number;
        Name: string;
        Description: string;
        Body: string;
    };
}

export interface NewsletterSendMethod {
    Id: number;
    Name: string;
    Description: string;
}

export interface BillingInformation {
    CompanyId: string;
    BillingMethodId: number;
    Name: string;
    Attention: string;
    Street1: string;
    Street2: string;
    ZipCode: string;
    City: string;
    CountryId: string;
    Email: string;
    ContactEmail?: string;
    PaymentTermsDays: number;
    VatRegistrationNumber: string;
    BillingMethodOptions: [
        {
            BillingMethodCountriesRelation: [
                {
                    BillingMethodId: number;
                    CountryId: string;
                }
            ];
            Id: number;
            Name: string;
            Description: string;
        }
    ];
}

export interface License {
    Id: string;
    CompanyId: string;
    TypeId: number;
    Meta: string;
    BillingInformation: BillingInformation;
    Canceled: boolean;
    LicenseStatus?: 'Active' | 'Scheduled' | 'Cancelled' | 'Ended';
    Type: LicenseType;
    ValidFrom: string;
    ValidTo: string;
    // Plan the license is billed on (or will be, for Scheduled rows). Null on some backfilled rows.
    LicensePlanId?: number | null;
    Active?: boolean;
    MetaData?: string;
    // VOSS subscription the license is billed under. Omitted by the API when null.
    VossSubscriptionId?: string | null;
    // Only present when the license list was fetched with IncludeVossSubscription=true.
    VossSubscription?: {
        SubscriptionBillingPeriodStartDate?: string;
        SubscriptionBillingPeriodEndDate?: string;
        // End of the VOSS binding period. PLACEHOLDER name: not yet returned by legacy-api.
        BindingPeriodEndDate?: string;
    } | null;
}

export interface Resource {
    Id: number;
    Name: string;
    Description: string;
    Color: string;
    ImageUrl?: string;
    Email: string;
    MobilePhone: string;
    EmailNotification: boolean;
    SMSNotification: boolean;
    EmailReminder: boolean;
    SMSReminder: boolean;
    Active: boolean;
    ResourceCategory?: number;
    RelayStartsBeforeMinutes?: number;
}

export interface ResourceType {
    Id: number;
    CompanyId: string;
    Name: string;
    Resources: Resource[];
    Description: string;
    Active: boolean;
}

export interface PriceBase {
    CompanyId: string;
    Id: number;
    ServiceId: number;
    CalculationTypeId: number;
    Price: number;
    CurrencyId: string;
    PriceSign: string;
    VAT: number;
    Category: string;
    PriceText: string;
    From: Date | string;
    To: Date | string;
    FromTime: string;
    ToTime: string;
    Service: {
        Id: number;
        Name: string;
        Description: string;
        ImageUrl: {};
        LengthInMinutes: number;
        MaxNumberOfSpotsPerBooking: number;
        IsGroupBooking: boolean;
        IsPaymentEnabled: boolean;
    };
    IsTimeSpecific: boolean;
    IsDaysOfWeekSpecific: boolean;
}

export interface Price extends PriceBase {
    DaysOfWeek: Array<{
        DayOfWeekId: number;
        DotNetDayOfWeekId: number;
        DayOfWeek: string;
    }>;
}

export interface PriceWithPrimitiveDaysOfWeek extends PriceBase {
    DaysOfWeek: number[];
}

export interface ServiceResourceType extends ResourceType {
    SelectableByUser: boolean;
}

export interface Service {
    Id: number;
    PriceViewTypeId: number;
    Name: string;
    Description: string;
    ImageUrl?: string;
    TotalSpots: number;
    LockSpotsToBooking: boolean;
    MaxNumberOfSpotsPerBooking: number;
    MinNumberOfResourcesToBook: number;
    MaxNumberOfResourcesToBook: number;
    UnbookBeforeDays: number;
    UnbookBeforeHours: number;
    UnbookBeforeMinutes: number;
    ScheduleType: string;
    ScheduleTypeId: number;
    BookBeforeDays: number;
    BookBeforeHours: number;
    BookBeforeMinutes: number;
    Group: string;
    SortOrder: number;
    Active: boolean;
    IsGroupBooking: boolean;
    GroupBooking: {
        Active: boolean;
        Min: number;
        Max: number;
    };
    MultipleResource: {
        Active: boolean;
        Min: number;
        Max: number;
    };
    IsPaymentEnabled: boolean;
    MaxPaymentTime: number;
    OnlyVisibleByAdmin: boolean;
    Duration: number;
    MinDuration: number;
    MaxDuration: number;
    DurationInterval: number;
    DurationTypeId: number;
    PauseAfterBooking: number;
    CustomFields: Array<CustomField>;
    CustomFieldValues: Array<CustomFieldValueType>;
    Prices: Array<Price>;
    Schedules: {
        ScheduleType: string;
        RecurringSchedules: RecurringSchedule[];
        DateSchedules: DateSchedule[];
    };
    ResourceTypes: ServiceResourceType[];
    EnableBookingQueue: boolean;
    EnableGoogleMeetBooking?: boolean;
}

export interface ServiceGroup {
    Name: string;
}

export interface Statistics {
    NumberOfOccuringBookings: number;
    NumberOfCreatedBookings: number;
    NumberOfCreatedCustomers: number;
    NumberOfCanceledBookings: number;
    OccuringBookingsByDay: Array<{ Date: Date | string; Value: number }>;
    CreatedBookingsByDay: Array<{ Date: Date | string; Value: number }>;
    CreatedCustomersByDay: Array<{ Date: Date | string; Value: number }>;
    CanceledBookingsByDay: Array<{ Date: Date | string; Value: number }>
}

export interface SupportTicket extends CreatedUpdated {
    Id: number;
    CompanyUserId: string;
    Title: string;
    Description: string;
    CaseStatusId: number;
    CaseTypeId: number;
    CaseAreaId: number;
    CreatedBy: string;
    UpdatedBy: string;
    CaseStatus: {
        Id: number;
        Name: string;
        Description: string;
        Icon: string;
        Color: string;
    };
    CaseType: { Id: number; Name: string; Description: string };
    CaseArea: { Id: number; Name: string; Description: string };
    Comments: [
        {
            SupportCaseId: number;
            Id: number;
            Comment: string;
            CreatedBy: string;
            Created: string;
        }
    ];
}

export interface SupportTicketArea {
    Id: number;
    Name: string;
    Description: string;
}
export interface SupportTicketStatus {
    Id: number;
    Name: string;
    Description: string;
    Icon: string;
    Color: string;
}

export interface SupportTicketType {
    Id: number;
    Name: string;
    Description: string;
}

export interface SupportTicketComment {
    SupportCaseId: number;
    Id: number;
    Comment: string;
    CreatedBy: string;
    Created: Date | string;
}

export interface SupportTicketAttachment {
    Id: number;
    FileUrl: string;
}

export interface PromoCode {
    Id: number;
    CompanyId: number;
    MaxNumberOfUses: number;
    MaxNumberOfUsesPerCustomer: number;
    ValidFrom: Date | string;
    FromTime: string;
    ValidTo: Date | string;
    ToTime: string;
    RebateCodeTypeId: number;
    RebateCodeValue: number;
    RebateCodeSign: string;
    AutoGenerateRebateCodeSign: boolean;
    PersonalNote: string;
    DaysOfWeek: number[];
    Services: number[];
    CustomersIds: number[];
    PaymentReceived: boolean;
}

export type PriceDetail = definitions['TotalPricePriceDetail'];

export type CalculatedPrice = definitions['TotalPriceInformationResponse'];
export type CalculatePriceQuery = definitions["CalculateTotalPriceOnService"];
export interface Quantity {
    Id?: number;
    Quantity: number;
    Price: number;
    PriceId?: number;
    CurrencyId: string;
    PriceSign?: string;
    Category?: string;
    VAT?: number;
    PriceText?: string;
    OccupiesSpot?: boolean;
}

export type TrialType = any;

export type LicenseExceededPayload = any;

export interface AgreementModalData {
    header: string;
    description: string;
    content: string;
}

export interface BillingModalData {
    content: string;
}

export type LicensesInformation = {
    Name: string;
    Description: string;
    FreeEdition: boolean;
    StandardEdition: boolean;
    ProfessionalEdition: boolean;
    BusinessEdition: boolean;
    EnterpriseEdition: boolean;
    FreeEditionValue: string;
    StandardEditionValue: string;
    ProfessionalEditionValue: string;
    BusinessEditionValue: string;
    EnterpriseEditionValue: string;
    Url: string;
    LicenseInformationTypeId: number;
};

export type CompanyLicenses = {
    [key: string]: License;
};

export interface ErrorCheckObject {
    ErrorMessage: string;
}

export interface RecurringSchedule {
    Id: number;
    Name: string;
    Description: string;
    Active: boolean;
    TimeInterval: number;
    ValidFrom: Date | string;
    ValidTo: Date | string;
    StartTime: string;
    EndTime: string;
    NumberOfScheduleDays: number;
    IsResourceSpecific: boolean;
    UpdatedDate: Date | string;
    CreatedDate: Date | string;
    EnableBookingUntilClosingTime: boolean;
    DaysOfWeek: number[];
    Resources: [
        {
            Id: number;
            Name: string;
            ImageUrl: string;
        }
    ];
    Services: [
        {
            Id: number;
            Name: string;
            ImageUrl: string;
        }
    ];
    Exceptions: [
        {
            StartTime: string;
            EndTime: string;
        }
    ];
    ScheduleDates: [
        {
            Id: number;
            Date: Date | string;
            StartTime: string;
            EndTime: string;
        }
    ];
}

export interface SystemSettings {
    CompanyId: string;
    Active: boolean;
    InactiveMessage: string;
    Searchable: boolean;
    GATrackingId: string;
    GTMTrackingId: string;
    FacebookPixelId: string;
    MultiLanguage: boolean;
    EnableAPITranslation: boolean;
    DefaultLanguage: string;
}

export interface Time {
    From: Date | string;
    To: Date | string;
    Free: number;
    FreeSpots: number;
}

export interface TimeException {
    Id: number;
    CompanyId: string;
    Locked: boolean;
    ResourceIds: number[];
    Resources: Resource[];
    IsRecurring: boolean;
    From: Date | string;
    To: Date | string;
    FromTime: string;
    ToTime: string;
    ReasonText: string;
    ReasonTextPublic: string;
    Color: string;
    BlockTime: boolean;
    Private: boolean;
    CalendarExportStatus: {
        CalendarId: string;
        ExceptionId: number;
        Synced: boolean;
    };
    DaysOfWeek: [
        {
            DayOfWeekId: number;
            DotNetDayOfWeekId: number;
            DayOfWeek: string;
        }
    ];
    Created: Date | string;
    Tags?: Tag[];
}

export interface RatingSummary {
    AverageScore: number;
    RatingScore1Count: number;
    RatingScore2Count: number;
    RatingScore3Count: number;
    RaingScore4Count: number;
    RatingScore5Count: number;
    Count: number;
}

export interface Rating {
    CompanyId: string;
    BookingId: number;
    Identifier: string;
    RatingScore: number;
    CreatedDate: string;
    Status: number;
    UpdatedDate: string;
    Review: Review;
}

export interface Review {
    Title: string;
    Description: string;
    Author: string;
    ReviewAnswer: string;
}

export interface RatingsResponse {
    Offset: number;
    Total: number;
    Results: Array<Rating>;
}

export interface Paging<T> {
    Offset: number;
    Total: number;
    Results: Array<T>;
}

export interface AccessKeyType {
    Id: number;
    KeyType: string;
    Description: string;
}

export interface AccessKey {
    Id: string;
    CompanyId: string;
    AccessKeyTypeId: number;
    Value: string;
    CustomerId: string;
}

export type PriceWithReference = Price & { ExternalReference: string };

export type EaccountingArticle = definitions['EAccountingArticleQueryResponse'];

export interface ArticlePrice {
    Id: number | string;
    Name: string;
    Description: string;
    PriceId: string;
    // ArticleId
    ExternalReference: string;
    PriceText: string;
}

export type ServicePriceMapping = operations["EAccountingUpdatePricesMappingarticlemappings_Create"]['responses']['200']['schema'];

export type ExternalArticleReferenceType = 'EAccountingArticle' | 'StripeArticle';

export interface ArticleMapping {
    Id: string;
    CompanyId: string;
    ArticleId: number;
    ExternalReference: string;
    ReferenceType: ExternalArticleReferenceType;
}

export interface ArticleMappingToHandle {
    Id?: string;
    ArticleId: number;
    ExternalReference: string;
    ReferenceType: ExternalArticleReferenceType;
}

export interface GetArticleMappingsQuery {
    CompanyId?: string;
    ReferenceType?: ExternalArticleReferenceType;
    ArticleId?: number;
}

export interface UpdateArticleMappingsQuery {
    CompanyId?: string;
    ArticleMappings: ArticleMappingToHandle[];
}
export interface DefaultPriceArticle {
    Name: string;
    Description: string;
    VatPercent: number;
    CurrencySign: string;
    ExternalReference: string;
}
export interface AuthData {
    UserId: string;
    UserName: string;
    DisplayName: string;
    BearerToken: string;
    RefreshToken: string;
    ProfileUrl: string;
    SessionId: string;
    Roles: string[];
    Meta: AuthMeta;
}

export interface AuthMeta {
    CompanyId: string;
    Id: string;
    Firstname: string;
    Lastname: string;
    Email: string;
    Phone: string;
    CompanyUsers: AuthCompanyUser[];
}

export interface AuthCompanyUser {
    CompanyId: string;
    CustomerId: string;
    CustomFieldValues: any[];
}

export type EAccountingInvoiceSendTypes = '0' | '1' | '2' | '3';

export interface AutoInvoicePrintFields {
    InvoiceAddress1: string;
    InvoiceAddress2: string;
    InvoiceCity: string;
    InvoicePostalCode: string;
    InvoiceCountryCode: string;
}

export interface PaymentTerm {
    Id: string;
    Name: string;
    NameEnglish: string;
    NumberOfDays: number;
    TermsOfPaymentTypeId: number;
    TermsOfPaymentTypeText: string;
    AvailableForSales: boolean;
    AvailableForPurchase: boolean;
}

enum RotPropertyTypes {
    Apartment = 1,
    Property = 2,
}

enum RotReducedInvoicingTypes {
    Normal = 0,
    Rot = 1,
    Rut = 2,
}

export interface CreateInvoiceType extends Partial<AutoInvoicePrintFields> {
    termsOfPaymentId?: string;
    CompanyId?: string;
    BookingId?: number;
    TermsOfPaymentId?: string;
    Notes?: string[];
    RotReducedInvoicingType?: RotReducedInvoicingTypes;
    RotPropertyType?: RotPropertyTypes;
    SendType?: EAccountingInvoiceSendTypes;
}

export interface EaccountingInvoice {
    BookingId: number;
    InvoiceId: string;
    CreatedDate: string;
    TotalAmount: number;
    TotalVatAmount: number;
    CustomerId: string;
    Rows: EaccountingRow[];
    VatSpecification: EaccountingVatSpecification[];
    InvoiceDate: string;
    DueDate: string;
    Persons: any[];
    InvoiceCustomerName: string;
    InvoiceAddress: EaccountingInvoiceAddress;
    CustomerIsPrivatePerson: boolean;
    TermsOfPaymentId: string;
    CustomerEmail: string;
    InvoiceNumber: number;
    CustomerNumber: string;
    CreatedUtc: string;
    ModifiedUtc: string;
    IncludesVat: boolean;
    IsSold: boolean;
    PaymentStatus: string;
    PriceSign: string;
    CreditedBy: any[];
}

export type CreateInvoiceResponse = RecursiveRequired<operations['CreateInvoiceinvoice_Post']['responses']['200']['schema']>;

export interface EaccountingRow {
    ArticleNumber?: string;
    ArticleId?: string;
    IsServiceArticle: boolean;
    AmountNoVat: number;
    PercentVat: number;
    LineNumber: number;
    IsTextRow: boolean;
    Text: string;
    UnitPrice: number;
    UnitAbbreviation?: string;
    UnitAbbreviationEnglish?: string;
    DiscountPercentage: number;
    Quantity: number;
    IsWorkCost: boolean;
    IsVatFree: boolean;
    UnitId?: string;
    WorkCostType: number;
    WorkHours?: number;
    MaterialCosts?: number;
    GreenTechnologyType: string;
    ContributionMargin: EaccountingContributionMargin;
}

export interface EaccountingContributionMargin { }

export interface EaccountingVatSpecification {
    AmountInvoiceCurrency: number;
    VatAmountInvoiceCurrency: number;
    VatPercent: number;
}

export interface EaccountingInvoiceAddress {
    InvoiceAddress1: string;
    InvoiceAddress2: string;
    InvoiceCity: string;
    InvoicePostalCode: string;
    InvoiceCountryCode: string;
}

export type CodelockSettings = definitions['CodeLockSettingResponse'];


export interface UpdateEaccountingSettingsParams {
    Active: boolean;
    CompanyId: string;
    DefaultCreateType: DefaultCreateTypes;
    DefaultArticle0PercentVat?: string;
    DefaultArticle6PercentVat?: string;
    DefaultArticle12PercentVat?: string;
    DefaultArticle25PercentVat?: string;
    DefaultTermsOfPaymentId: string;
}

export interface EaccountingSettingsResponse {
    CompanyId: string;
    Active: boolean;
    DefaultCreateType: DefaultCreateTypes;
    DefaultArticle0PercentVat: string;
    DefaultArticle6PercentVat: string;
    DefaultArticle12PercentVat: string;
    DefaultArticle25PercentVat: string;
    DefaultTermsOfPaymentId: string;
    CreatedDate: string;
    UpdatedDate: string;
}
export type BookingUserQueueItemResponse = {
    BookingUserQueue: paths['/bookinguserqueue']['post']['responses']['200']['schema'];
    ResponseStatus: {
        Message: string;
    };
};

export type CreateBookingsResponse = definitions['CreateBookingsResponse'];

export type CreateBookingOrQueue = (BookingUserQueueItemResponse & CreateBookingsResponse);

export type ApiError = {
    ResponseStatus?: definitions['ResponseStatus']
}

export type Args<T> = T extends (...args: infer U) => infer R
    ? { args: U, result: R }
    : never;

export type PriceTimeUnit = "minute" | "hour" | "day" | "service";


export type DefaultCreateTypes = 'Draft' | 'CreateSend';



export type Articles = Required<paths['/articles']['get']['responses']['200']['schema']>;
export type Article = definitions['ArticleResponse'];
export type MappableArticle = Article & { Id: number };
export type GetArticlesQuery = Partial<paths['/articles']['get']['parameters']['query']>;
export type CreateArticleQuery = paths['/articles']['post']['parameters']['body']['body'];



// export type RebateCodeResponse = Required<definitions["QueryResponse_RebateCodeResponse_"]>;
// export type RebateCode = RebateCodeResponse['Results'][0];

export type RebateCodeResponse = Required<definitions["QueryResponse_RebateCodeResponse_"]>;
export type RebateCode = Required<definitions["RebateCode"]>;
export type RebateGetBySignResponse = definitions["RebateCodeResponse"];
export type RebateCodePrice = definitions["RebateCodePrice"];

export type GetRebateCodesQuery = paths['/rebatecodes']['get']['parameters']['query'];
export type UpdateRebateCodeQuery = definitions["CreateRebateCode"];
export type CreateRebateCodeQuery = definitions["CreateRebateCode"];
export type DeleteRebateCodeQuery = definitions["CreateRebateCode"];

export type GetRebateCodeBySignQuery = paths['/rebatecodes/getbysign']['get']['parameters']['query'];

export type RebateCodeTransaction = Required<definitions["RebateCodeTransaction"]>;

// OpenAPI-derived helper types
// /articles/types returns items shaped like { Id, Name, Description }
export interface ArticleType {
    Id: number;
    Name: string;
    Description?: string;
}

export type CreateServicePrice = definitions['CreateServicePrice'];
export type CreateServiceQuery = definitions['CreateService'];

export interface MailchimpSyncLog {
    Id: number;
    SuccessCount: number;
    Status: string;
    Errors: string[];
    CreatedDate: string;
}

export type GetMailchimpAudienceQuery = paths['/mailchimp/audience/company']['get']['parameters']['query'];
export type MailchimpAudienceResponse = paths['/mailchimp/audience/company']['get']['responses']['200']['schema'];



export type CurrencyQuery = Partial<operations["CurrencyQuery_Get"]['parameters']['query']>;
export type Currency = Required<definitions['Currency']>

export type PromoCodesQuery = paths['/rebatecodes']['get']['parameters']['query'];
export type PromoCodeSchema = RecursiveRequired<paths['/rebatecodes']['get']['responses']['200']['schema']>;
export type PromoCodeDefinition = PromoCodeSchema['Results'][0];

export type TotalPriceInformationResponse = RecursiveRequired<definitions['TotalPriceInformationResponse']>

export type SyncmailchimpQuery = paths['/mailchimp/member/company/customer/sync']['put']['parameters']['body'];
export type SyncMailchimpResponse = paths['/mailchimp/member/company/customer/sync']['put']['responses']['200']['schema'];

export type WebhookSettingsQuery = paths['/webhook/settings']['put']['parameters']['body'];
export type WebhookSettingsResponse = paths['/webhook/settings']['put']['responses']['200']['schema'];
export type GetWebhookEndointsQuery = paths['/webhook/endpoints']['get']['parameters']['query'];
export type GetWebhookEndointsResponse = paths['/webhook/endpoints']['get']['responses']['200']['schema'];
export type GetWebhookEventTypesResponse = definitions["WebhookEventTypesResponse"];
export type WebhookEndpoint = Required<GetWebhookEndointsResponse>['Endpoints'][0];
export type UpdateWebhookEndointsQuery = paths['/webhook/endpoints']['post']['parameters']['query'];
export type UpdateWebhookEndointsResponse = paths['/webhook/endpoints']['post']['responses']['200']['schema'];
export type GetWebhookLogsQuery = paths['/webhook/messages']['get']['parameters']['query'];
export type GetWebhookLogsResponse = paths['/webhook/messages']['get']['responses']['200']['schema'];
export type WebhookLog = definitions['GetMessageResponseDto'];

/** Webhook endpoint attempt (delivery attempt). API: ListWebhookEndpointAttempts */
export interface WebhookEndpointAttempt {
    Id?: string;
    MessageId?: string;
    EndpointId?: string;
    Url?: string;
    Status?: string;
    ResponseStatusCode?: number;
    /** Format: date-time */
    Timestamp?: string;
    TriggerType?: string;
    ErrorMessage?: string;
    ResponseBody?: string;
    [key: string]: unknown;
}
export interface GetWebhookEndpointAttemptsResponse {
    Attempts?: WebhookEndpointAttempt[];
    Data?: WebhookEndpointAttempt[];
}

/** Webhook message attempt (delivery attempt for a message). API: ListWebhookMessageAttempts */
export interface WebhookMessageAttempt {
    Id?: string;
    MessageId?: string;
    EndpointId?: string;
    Url?: string;
    Status?: string;
    ResponseStatusCode?: number;
    /** Format: date-time */
    Timestamp?: string;
    TriggerType?: string;
    ErrorMessage?: string;
    ResponseBody?: string;
    [key: string]: unknown;
}
export interface GetWebhookMessageAttemptsResponse {
    Attempts?: WebhookMessageAttempt[];
    Data?: WebhookMessageAttempt[];
}

export enum AxemaUserSyncStatus {
    Pending = 0,
    Processing = 1,
    Completed = 2,
    Failed = 3,
}

export interface AxemaUserSyncJob {
    Id: string;
    CompanyId: string;
    Status: AxemaUserSyncStatus;
    StatusName: string;
    UsersFound?: number | null;
    UsersCreated?: number | null;
    UsersUpdated?: number | null;
    ErrorMessage?: string | null;
    CreatedDate: string;
    UpdatedDate: string;
}

export interface CreateAxemaUserSyncQuery {
    CompanyId?: string;
}

export interface QueryAxemaUserSyncJobsQuery {
    CompanyId?: string;
    Status?: AxemaUserSyncStatus;
}

export interface QueryAxemaUserSyncJobsResponse {
    Results: AxemaUserSyncJob[];
    ResponseStatus?: ApiError['ResponseStatus'];
}

// NOTE: Incentives are handled by a separate backend not included in the generated OpenAPI spec.
// Keep these as `any` so the rest of the app can compile.
export type IncentiveResponse = any;
export type Incentive = any;

export enum SendReportMethodEnum {
    Send = 1,
    Print = 2
}

export type ActionType = 'Upgrade' | 'AddOn' | 'Information';

export type IncentiveUpgradeActionPayload = any;

export interface Item {
    Id: number
    Name: string
    AllowedItems: number
}

// eslint-disable-next-line no-redeclare
export interface Price {
    Country: Country
    MonthlyPayment: boolean
    LicenseTypeId: number
    CountryId: string
    Price: number
    ModifiedDate: string
}

// eslint-disable-next-line no-redeclare
export interface Country {
    CurrencyId: string
    CurrencyInfo: CurrencyInfo
    Name: string
    TimeZone: string
    ModifiedDate: string
    Id: string
}

export interface CurrencyInfo {
    Name: string
    CurrencySign: string
    Active: boolean
    ModifiedDate: string
    Id: string
}


type RecursiveRequired<T> = Required<{
    [P in keyof T]: T[P] extends object | undefined ? RecursiveRequired<Required<T[P]>> : T[P];
}>;

export interface Option {
    Id: number;
    Name: string;
    Description: string;
}

export type GetResourcesResponse = definitions["QueryResponse_ResourceQueryResponse_"];

export type LicensePrice = definitions["LicensePrice"];

export interface GetSimilarServicesResponse {
    Success: boolean
    GeneratedAt: string
    ServiceIds: number[]
    SimilarServices: SimilarService[]
    ModelUsed: string
    PromptUsed: string
}

export interface SimilarService {
    Name: string
    Description: string
    Category: string
    SuggestedPrice: number
    SuggestedDurationMinutes: number
    ConfidenceScore: number
}

export interface Payment {
    Id: number;
    InternalReference: number;
    PaymentReferenceId: string;
    OrderItemReferenceId: string;
    PaymentProviderId: number;
    Amount: number;
    ArticleType: {
        ArticleTypeId: number;
        ArticleTypeName: string;
        ArticleTypeDescription: string;
    };
    VAT: number;
    AmountCredited: number;
    CurrencyId: string;
    CurrencyInfo: {
        Id: string;
        Name: string;
        CurrencySign: string;
    };
    Created: string;
    Updated: string;
}

export interface ErrorLogMessage {
    ErrorMessage: string;
}

// ─── Tags ─────────────────────────────────────────────────────────────────────

export enum TagScope {
    Booking = 0,
    Customer = 1,
    TimeException = 2,
}

export interface Tag {
    Id: number;
    CompanyId: string;
    Name: string;
    Scope: TagScope;
    Color: string | null;
    SortOrder: number;
    Active: boolean;
    CreatedDate: string;
}

export interface GetTagsQuery {
    CompanyId?: string;
    Scope?: TagScope;
    Active?: boolean;
    Skip?: number;
    Take?: number;
}

export interface CreateTagBody {
    CompanyId?: string;
    Name: string;
    Scope: TagScope;
    Color?: string;
    SortOrder?: number;
}

export interface UpdateTagBody {
    CompanyId?: string;
    Id: number;
    Name?: string;
    Color?: string;
    SortOrder?: number;
    Active?: boolean;
}

export interface TagsResponse {
    Results: Tag[];
    Total: number;
    Offset: number;
}

export interface AddTagsToBookingBody {
    CompanyId?: string;
    BookingId: number;
    TagIds: number[];
}

export interface RemoveTagFromBookingParams {
    CompanyId?: string;
    BookingId: number;
    TagId: number;
}

export interface AddTagsToCustomerBody {
    CompanyId?: string;
    CustomerId: string;
    TagIds: number[];
}

export interface RemoveTagFromCustomerParams {
    CompanyId?: string;
    CustomerId: string;
    TagId: number;
}

export interface AddTagsToTimeExceptionBody {
    CompanyId?: string;
    TimeExceptionId: number;
    TagIds: number[];
}

export interface RemoveTagFromTimeExceptionParams {
    CompanyId?: string;
    TimeExceptionId: number;
    TagId: number;
}

export interface CustomerSearchResult {
    Id: string;
    Firstname: string;
    Lastname: string;
    Email: string;
    Phone: string;
}

// Summarized Invoices
export interface CreateSummarizedInvoiceParams {
    CompanyId?: string;
    DateFrom: string;
    DateTo: string;
    CustomerId?: string;
    ServiceId?: number;
    BookingStatusIds?: number[];
    TermsOfPaymentId: string;
    CreateType?: string;
    SendType?: number;
    CorporateIdentityNumber?: string;
    InvoiceAddress1?: string;
    InvoiceAddress2?: string;
    InvoiceCity?: string;
    InvoicePostalCode?: string;
    InvoiceCountryCode?: string;
}

export interface SummarizedInvoiceJobResponse {
    SummarizedInvoiceId: string;
    CustomerId: string;
    CustomerName: string;
    BookingCount: number;
}

export interface SummarizedInvoiceCreateResponse {
    TotalCustomersQueued: number;
    Jobs: SummarizedInvoiceJobResponse[];
    ResponseStatus?: any;
}

export interface GetSummarizedInvoicesParams {
    CompanyId?: string;
    CustomerId?: string;
    DateFrom?: string;
    DateTo?: string;
    Status?: number;
}

export interface SummarizedInvoiceDetail {
    Id: string;
    CompanyId: string;
    CustomerId: string;
    CustomerName: string;
    EAccountingInvoiceId?: string;
    Draft: boolean;
    DateFrom: string;
    DateTo: string;
    ServiceId?: number;
    Status: number;
    StatusName: string;
    ErrorMessage?: string;
    BookingIds: number[];
    CreatedDate: string;
    UpdatedDate: string;
}

export interface SummarizedInvoicesResponse {
    Results: SummarizedInvoiceDetail[];
    ResponseStatus?: any;
}

export interface PreviewSummarizedInvoiceParams {
    CompanyId?: string;
    DateFrom: string;
    DateTo: string;
    CustomerId?: string;
    ServiceId?: number;
    BookingStatusIds?: number[];
    SendType?: number;
}

export interface PreviewCustomerGroup {
    CustomerId: string;
    CustomerName: string;
    Email?: string;
    BookingCount: number;
    BookingIds: number[];
    MissingInvoiceAddress: boolean;
    MissingEmail: boolean;
    InvoiceAddress1?: string;
    InvoiceAddress2?: string;
    InvoiceCity?: string;
    InvoicePostalCode?: string;
    InvoiceCountryCode?: string;
    CorporateIdentityNumber?: string;
}

export interface PreviewSummarizedInvoiceResponse {
    TotalCustomers: number;
    TotalBookings: number;
    Customers: PreviewCustomerGroup[];
    ResponseStatus?: any;
}

export interface CompanyFeatureFlagResponse {
    Id: number;
    Name: string;
    IsEnabled: boolean;
    IsPublic: boolean;
    CompanyAdminCanToggle: boolean;
}

// GET /apikeys — list the API keys for the logged in user's company.
// Defined manually until the backend OpenAPI is regenerated into apitypes.ts.
export interface GetApiKeysQuery {
    CompanyId?: string;
    ActiveOnly?: boolean;
}

export interface ApiKey {
    CompanyId: string;
    ApiKey: string;
    Active: boolean;
    CreatedDate: string;
    ExpiryDate?: string;
    ContactEmail?: string;
    Notes?: string;
    AllowedIpAddresses?: string;
}

export interface ApiKeyQueryResponse {
    ApiKeys: ApiKey[];
    ResponseStatus?: any;
}

// POST /apikeys — create a new API key for the logged in user's company.
export interface CreateApiKeyBody {
    CompanyId?: string;
    ContactEmail?: string;
    Notes?: string;
}

// GET /feature/{companyId} — feature flags resolved for a company.
export interface CompanyFeatureFlag {
    Id: number;
    Name: string;
    IsEnabled: boolean;
    IsPublic: boolean;
    CompanyAdminCanToggle: boolean;
}

export interface CompanyFeatureFlagsResponse {
    Results: CompanyFeatureFlag[];
    Total: number;
    Offset: number;
}
